import React, { useState, useRef, useEffect } from 'react';
import { FaMicrophone, FaFileAudio, FaPlayCircle, FaRegFileAlt, FaRegListAlt, FaClipboard, FaFileWord, FaFilePdf, FaEllipsisV } from 'react-icons/fa';
import { toast } from 'sonner';
// Import jsPDF correctly with autoTable plugin
import { jsPDF } from 'jspdf';
// We need to import the autoTable plugin explicitly and properly
import autoTable from 'jspdf-autotable';

const AudioTranscriber = () => {
  const fileInputRef = useRef();
  const [progress, setProgress] = useState(0);
  const [transcription, setTranscription] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [fileName, setFileName] = useState('');
  const [transcriptionDropdownOpen, setTranscriptionDropdownOpen] = useState(false);
  const [summaryDropdownOpen, setSummaryDropdownOpen] = useState(false);

  const transcriptionDropdownRef = useRef(null);
  const summaryDropdownRef = useRef(null);

  useEffect(() => {
    // Reset animation state first
    setIsLoaded(false);

    // Small delay to ensure reset takes effect before starting animations
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Close dropdowns when clicking outside
    function handleClickOutside(event) {
      if (transcriptionDropdownRef.current && !transcriptionDropdownRef.current.contains(event.target)) {
        setTranscriptionDropdownOpen(false);
      }
      if (summaryDropdownRef.current && !summaryDropdownRef.current.contains(event.target)) {
        setSummaryDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const chunkAudioFile = async (file, maxChunkDuration = 60) => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const buf = await file.arrayBuffer();
    const decoded = await audioCtx.decodeAudioData(buf);
    const chunkSize = maxChunkDuration * decoded.sampleRate;
    const chunks = [];

    for (let start = 0; start < decoded.length; start += chunkSize) {
      const end = Math.min(start + chunkSize, decoded.length);
      const chunk = audioCtx.createBuffer(
        decoded.numberOfChannels,
        end - start,
        decoded.sampleRate
      );
      for (let ch = 0; ch < decoded.numberOfChannels; ch++) {
        const seg = decoded.getChannelData(ch).subarray(start, end);
        chunk.copyToChannel(seg, ch);
      }
      chunks.push(chunk);
    }
    return chunks;
  };

  const audioBufferToWavBlob = (audioBuffer) => {
    const numChan = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);

    const writeStr = (off, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i));
    };

    writeStr(0, 'RIFF');
    view.setUint32(4, length - 8, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChan, true);
    view.setUint32(24, audioBuffer.sampleRate, true);
    view.setUint32(28, audioBuffer.sampleRate * numChan * 2, true);
    view.setUint16(32, numChan * 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, audioBuffer.length * numChan * 2, true);

    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let ch = 0; ch < numChan; ch++) {
        let sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
        sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }
    return new Blob([view], { type: 'audio/wav' });
  };

  const transcribeChunk = async (blob) => {
    const form = new FormData();
    form.append('file', blob);

    const res = await fetch('http://localhost:5000/api/audio/transcribe-chunk', { method: 'POST', body: form });
    if (!res.ok) throw new Error('Chunk transcription failed');
    return res.json();
  };

  const analyzeTranscription = async (fullText) => {
    const res = await fetch('http://localhost:5000/api/audio/analyze-transcription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fullText }),
    });
    if (!res.ok) throw new Error('Failed to analyze transcription');
    const data = await res.json();
    return data.summary;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const handleUpload = async () => {
    const file = fileInputRef.current.files[0];
    if (!file) return alert('Please select a file.');

    setLoading(true);
    setProgress(0);
    let fullText = '';

    try {
      const audioChunks = await chunkAudioFile(file);
      for (let i = 0; i < audioChunks.length; i++) {
        const wavBlob = audioBufferToWavBlob(audioChunks[i]);
        const { segments } = await transcribeChunk(wavBlob);
        segments.forEach(s => {
          const start = new Date(s.start * 1000).toISOString().substr(11, 8);
          const end = new Date(s.end * 1000).toISOString().substr(11, 8);
          fullText += `[${start} - ${end}]:\n${s.text.trim()}\n\n`;
        });
        setProgress(Math.round(((i + 1) / audioChunks.length) * 100));
      }

      const mom = await analyzeTranscription(fullText);
      setTranscription(fullText);
      setSummary(mom);

      // Show notification when transcription is complete
      toast.success('Transcription Complete', {
        description: `Audio file "${fileName}" has been successfully transcribed.`,
        duration: 5000
      });

    } catch (err) {
      console.error(err);
      alert('An error occurred. Check console.');
      toast.error('Transcription Failed', {
        description: 'An error occurred during the transcription process.',
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  // Extract content to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(
      () => {
        toast.success(`${type} Copied`, {
          description: `${type} has been copied to clipboard.`,
          duration: 3000
        });
      },
      (err) => {
        console.error('Could not copy text: ', err);
        toast.error('Copy Failed', {
          description: 'An error occurred while copying to clipboard.',
          duration: 3000
        });
      }
    );
  };

  // Generate and download Word document
  const downloadAsWord = (content, filename) => {
    // Create HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${filename}</title>
      </head>
      <body>
        <pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${content}</pre>
      </body>
      </html>
    `;

    // Create a Blob with the HTML content
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);

    // Create a link and trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`${filename} Downloaded`, {
      description: `${filename} has been downloaded as a Word document.`,
      duration: 3000
    });
  };

  // Generate and download PDF
  const downloadAsPDF = (content, filename) => {
    try {
      // Create new jsPDF instance
      const doc = new jsPDF();

      // Set title
      doc.setFontSize(16);
      doc.text(filename, 20, 20);

      // Add timestamp
      const now = new Date();
      doc.setFontSize(10);
      doc.text(`Generated on: ${now.toLocaleString()}`, 20, 30);

      // Split content by newlines to handle the formatting better
      const contentLines = content.split('\n');

      // Add content using text instead of autoTable
      doc.setFontSize(11);
      let yPos = 40;
      const maxWidth = 170;

      for (let i = 0; i < contentLines.length; i++) {
        const line = contentLines[i];
        if (line.trim() === '') {
          yPos += 5; // Add space for empty lines
          continue;
        }

        // Check if we need a new page
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }

        // Split long lines
        const splitText = doc.splitTextToSize(line, maxWidth);
        doc.text(splitText, 20, yPos);
        yPos += 6 * splitText.length; // Adjust spacing based on number of lines after splitting
      }

      // Save the PDF
      doc.save(`${filename}.pdf`);

      toast.success(`${filename} Downloaded`, {
        description: `${filename} has been downloaded as a PDF document.`,
        duration: 3000
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('PDF Generation Failed', {
        description: 'An error occurred while generating the PDF.',
        duration: 3000
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-3 md:p-6 lg:p-8">
      {/* Page Header */}
      <div className={`mb-4 transition-all duration-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1 flex items-center">
          <FaMicrophone className="mr-2" />
          MOM Generator
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Upload audio files for automatic transcription and meeting summaries.
        </p>
      </div>

      {/* Upload Card */}
      <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        {/* Card Header */}
        <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
          <h3 className="text-sm font-bold flex items-center">
            <FaFileAudio className="mr-1" />
            Audio Processing
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-3">
          <div className="mb-3">
            <label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
              Audio File
            </label>
            <div className="flex items-center space-x-2">
              <div className="relative flex-grow">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex items-center">
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 text-xs rounded-lg w-full p-2 flex items-center">
                    <FaFileAudio className="mr-2 text-gray-500 dark:text-gray-400" />
                    <span className="truncate">{fileName || "No file selected"}</span>
                  </div>
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="ml-2 px-3 py-2 text-xs font-medium text-white dark:text-black bg-gray-700 dark:bg-uasYellow rounded-lg hover:bg-gray-800 dark:hover:bg-yellow-500 focus:outline-none"
                  >
                    Browse
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center w-full">
            <button
              onClick={handleUpload}
              disabled={loading}
              className="relative inline-flex items-center justify-center overflow-hidden p-0.5 text-xs font-medium rounded-lg group bg-gradient-to-br from-black to-gray-800 dark:from-uasYellow dark:to-yellow-500 "
            >
              <span className="relative w-full px-3 py-2 transition-all ease-in duration-75 bg-black text-white dark:bg-transparent dark:text-black rounded-md group-hover:bg-opacity-0 group-hover:text-white dark:group-hover:text-black flex items-center justify-center">
                <FaPlayCircle className="mr-1" />
                {loading ? 'Processing...' : 'Start Transcription'}
              </span>
            </button>
          </div>

          {loading && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Processing audio...</span>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  className="bg-black dark:bg-uasYellow h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transcription & Summary Section - Side by side layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 flex-grow" style={{ minHeight: "300px", height: "calc(100vh - 300px)" }}>
        {/* Transcription Card */}
        <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-200 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center">
              <FaRegFileAlt className="mr-1" />
              Transcription
            </h3>
            {transcription && (
              <div className="relative" ref={transcriptionDropdownRef}>
                <button
                  onClick={() => setTranscriptionDropdownOpen(!transcriptionDropdownOpen)}
                  className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                >
                  <FaEllipsisV size={14} />
                </button>
                {transcriptionDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                    <div className="py-1">
                      <button
                        onClick={() => copyToClipboard(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaClipboard className="mr-2" />
                        Copy to Clipboard
                      </button>
                      <button
                        onClick={() => downloadAsWord(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFileWord className="mr-2" />
                        Download as Word
                      </button>
                      <button
                        onClick={() => downloadAsPDF(transcription, 'Transcription')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFilePdf className="mr-2" />
                        Download as PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 flex-grow flex flex-col">
            {transcription ? (
              <div className="relative h-full w-full">
                <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-800 dark:text-gray-200 overflow-y-scroll border border-gray-200 dark:border-gray-700 whitespace-pre-wrap absolute inset-0">{transcription}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 text-center flex items-center justify-center h-full">
                Transcription will appear here after processing
              </div>
            )}
          </div>
        </div>

        {/* Summary Card */}
        <div className={`bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md transition-all duration-500 delay-300 flex flex-col h-full ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="rounded-t-lg px-3 py-2 bg-black dark:bg-uasYellow text-white dark:text-black border-b flex items-center justify-between">
            <h3 className="text-sm font-bold flex items-center">
              <FaRegListAlt className="mr-1" />
              Minutes of the Meeting
            </h3>
            {summary && (
              <div className="relative" ref={summaryDropdownRef}>
                <button
                  onClick={() => setSummaryDropdownOpen(!summaryDropdownOpen)}
                  className="text-white dark:text-black hover:text-gray-200 dark:hover:text-gray-800 focus:outline-none"
                >
                  <FaEllipsisV size={14} />
                </button>
                {summaryDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                    <div className="py-1">
                      <button
                        onClick={() => copyToClipboard(summary, 'Meeting Summary')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaClipboard className="mr-2" />
                        Copy to Clipboard
                      </button>
                      <button
                        onClick={() => downloadAsWord(summary, 'Meeting_Summary')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFileWord className="mr-2" />
                        Download as Word
                      </button>
                      <button
                        onClick={() => downloadAsPDF(summary, 'Meeting_Summary')}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <FaFilePdf className="mr-2" />
                        Download as PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 flex-grow flex flex-col">
            {summary ? (
              <div className="relative h-full w-full">
                <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-800 dark:text-gray-200 overflow-y-scroll border border-gray-200 dark:border-gray-700 whitespace-pre-wrap absolute inset-0">{summary}</pre>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 text-center flex items-center justify-center h-full">
                Meeting summary will appear here after processing
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioTranscriber;